// Rotação de apresentação a partir de uma linha traçada no modelo.
//
// O utilizador pica dois pontos sobre o modelo (uma fachada, um eixo, um
// alinhamento de pilares) e essa direcção passa a ser a horizontal do
// ecrã nas vistas padrão. É o "norte do projecto" definido por uma
// referência do próprio desenho, em vez de um ângulo adivinhado.
//
// Nada disto toca no modelo: o resultado é só o ângulo que roda o
// significado de Frente, Trás, Dir e Esq. Ver `displayBearingDeg`.

export type P3 = [number, number, number]

/** Distância horizontal mínima entre os dois pontos, em metros.
 *
 *  Dois pontos quase na mesma vertical não definem direcção nenhuma: o
 *  ângulo entre eles seria ruído amplificado, e a vista dava um salto
 *  aparentemente aleatório. */
export const MIN_SPAN = 1e-3

/** Ângulo, em graus, que põe a direcção a→b horizontal no ecrã.
 *
 *  Só conta a componente HORIZONTAL: uma linha inclinada (o beirado de
 *  uma cobertura, uma rampa) alinha pela sua projecção em planta, que é
 *  o que se espera ao dizer "esta fachada fica horizontal".
 *
 *  Devolve null quando os dois pontos não definem direcção. */
export function bearingForHorizontal(a: P3, b: P3): number | null {
  const dx = b[0] - a[0]
  const dz = b[2] - a[2]
  if (Math.hypot(dx, dz) < MIN_SPAN) return null
  // Com a convenção do cubo (front = [sin θ, 0, cos θ]), a horizontal do
  // ecrã é (-cos θ, 0, sin θ). Igualá-la à direcção picada dá isto.
  const deg = (Math.atan2(dz, -dx) * 180) / Math.PI
  return ((deg % 360) + 360) % 360
}

/** A direcção que fica horizontal no ecrã para um dado ângulo.
 *
 *  Existe para o teste poder fechar o círculo: pegar num ângulo,
 *  perguntar que direcção ele torna horizontal, e confirmar que essa
 *  direcção devolve o mesmo ângulo. */
export function horizontalDirFor(deg: number): P3 {
  const r = (deg * Math.PI) / 180
  return [-Math.cos(r), 0, Math.sin(r)]
}

// --- Pôr a linha horizontal JÁ, na vista onde se está ------------------
//
// Definir o que "Frente" quer dizer não chega: quem traça uma linha
// espera ver a vista rodar naquele momento. Sem isto, o utilizador é
// avisado de que alinhou e não vê nada mudar, que foi exactamente o que
// aconteceu.
//
// Convenção da câmara, a mesma do cubo: azimute φ = atan2(-fx, -fz), com
// a câmara em (sin φ, ·, cos φ) em torno do alvo. A horizontal do ecrã é
// então (cos φ, 0, -sin φ).

/** Normaliza um ângulo em graus para (-180, 180]. */
export function wrapDeg(deg: number): number {
  const d = ((deg % 360) + 360) % 360
  return d > 180 ? d - 360 : d
}

/** Quanto rodar a câmara para a direcção a→b ficar horizontal no ecrã.
 *
 *  Devolve graus a somar ao azimute actual, já pelo caminho curto: a
 *  direcção oposta serve igualmente (uma linha horizontal continua
 *  horizontal vista ao contrário), portanto escolhe-se a das duas que
 *  mexe menos a vista. Sem isso, alinhar uma fachada podia dar meia
 *  volta ao edifício por nada.
 *
 *  Null quando os pontos não definem direcção. */
export function screenAlignDeltaDeg(
  azimuthDeg: number,
  a: P3,
  b: P3,
): number | null {
  const dx = b[0] - a[0]
  const dz = b[2] - a[2]
  if (Math.hypot(dx, dz) < MIN_SPAN) return null
  const querido = (Math.atan2(-dz, dx) * 180) / Math.PI
  const delta = wrapDeg(querido - azimuthDeg)
  const oposto = wrapDeg(delta + 180)
  return Math.abs(oposto) < Math.abs(delta) ? oposto : delta
}

/** Onde a câmara tem de ficar para a direcção a→b ser horizontal.
 *
 *  Devolve a posição, mantendo o alvo, a distância e a inclinação
 *  actuais: só o rumo muda. Devolver a POSIÇÃO em vez de um ângulo a
 *  somar é deliberado. Um `rotate` relativo depende da convenção de
 *  sinal da biblioteca de controlo, que não consigo verificar sem abrir
 *  isto num browser com um modelo; uma posição absoluta depende só desta
 *  função, que está testada.
 *
 *  Null quando os pontos não definem direcção. */
export function cameraPosForHorizontal(opts: {
  target: P3
  camera: P3
  a: P3
  b: P3
}): { pos: P3; azimuthDeg: number } | null {
  const dx = opts.b[0] - opts.a[0]
  const dz = opts.b[2] - opts.a[2]
  if (Math.hypot(dx, dz) < MIN_SPAN) return null

  const vx = opts.camera[0] - opts.target[0]
  const vy = opts.camera[1] - opts.target[1]
  const vz = opts.camera[2] - opts.target[2]
  const dist = Math.hypot(vx, vy, vz)
  if (dist < 1e-6) return null
  // Inclinação actual, preservada: alinhar não deve levantar nem baixar
  // a câmara, só rodá-la.
  const horiz = Math.hypot(vx, vz)
  const cosE = horiz / dist
  const sinE = vy / dist

  // Rumo em que a horizontal do ecrã, (cos φ, 0, -sin φ), fica paralela
  // à direcção picada. Das duas soluções escolhe-se a que deixa a
  // câmara mais perto de onde já está.
  const alvo = Math.atan2(-dz, dx)
  const actual = Math.atan2(vx, vz)
  const cand = [alvo, alvo + Math.PI]
  let phi = cand[0]
  let melhor = Infinity
  for (const c of cand) {
    const d = Math.abs(wrapDeg(((c - actual) * 180) / Math.PI))
    if (d < melhor) {
      melhor = d
      phi = c
    }
  }

  return {
    pos: [
      opts.target[0] + Math.sin(phi) * cosE * dist,
      opts.target[1] + sinE * dist,
      opts.target[2] + Math.cos(phi) * cosE * dist,
    ],
    // O rumo escolhido, para o cubo de navegação poder mostrar a face
    // alinhada com a linha traçada. Devolver o ângulo JUNTO com a
    // posição evita o erro de o recalcular noutra convenção, que foi o
    // que fez o cubo apontar para o sítio errado.
    azimuthDeg: ((((phi * 180) / Math.PI) % 360) + 360) % 360,
  }
}

/** Direcção horizontal de uma face vertical, a partir da sua normal.
 *
 *  Clicar UMA vez numa fachada dá a direcção exacta dela, sem depender
 *  de onde se clicou nem de haver snap: a normal é a mesma em toda a
 *  face. Dois pontos apanhados à mão sobre a mesma parede dão a mesma
 *  direcção com erro, e tanto mais quanto mais perto estiverem.
 *
 *  Null para superfícies horizontais (um pavimento, um tecto): não têm
 *  direcção horizontal e não servem para alinhar. */
export function faceHorizontalDir(normal: P3): P3 | null {
  // Perpendicular à normal, no plano horizontal.
  const dx = -normal[2]
  const dz = normal[0]
  if (Math.hypot(dx, dz) < 1e-3) return null
  return [dx, 0, dz]
}
